/**
 * P2P signaling client. Opens a WebSocket to `/api/p2p/ws?token=<jwt>`,
 * advertises the tracks the current viewer has cached offline, and exposes
 * `findPeer(trackId)` for the player to try a WebRTC handshake before
 * falling back to the network stream.
 *
 * This module owns the signaling only — the actual RTCPeerConnection dance
 * (offer/answer/ice + data-channel chunk transfer) lives in `p2p-transport`
 * once we wire it. Today, this is enough to observe traffic in the
 * server-side peer registry and coordinate opt-in tests.
 */
import { getAccess, getRefresh } from "./api";
import { listOffline } from "./offline";

type Msg =
  | { kind: "hello"; peer_id: string }
  | { kind: "peers"; track_id: string; peers: string[] }
  | { kind: "offer" | "answer"; from: string; sdp: string; track_id?: string }
  | { kind: "ice"; from: string; candidate: RTCIceCandidateInit; track_id?: string }
  | { kind: "pong" }
  | { kind: "error"; reason: string; detail?: string };

let ws: WebSocket | null = null;
let peerId: string | null = null;
let attempt = 0;
let openPromise: Promise<void> | null = null;
const listeners = new Set<(m: Msg) => void>();

function url(): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  const token = getAccess() ?? getRefresh() ?? "";
  return `${proto}//${location.host}/api/p2p/ws?token=${encodeURIComponent(token)}`;
}

export function connect(): Promise<void> {
  if (ws?.readyState === WebSocket.OPEN) return Promise.resolve();
  if (openPromise) return openPromise;
  openPromise = new Promise<void>((resolve, reject) => {
    ws = new WebSocket(url());
    ws.onopen = () => {
      attempt = 0;
      advertiseCurrent();
      resolve();
    };
    ws.onmessage = (ev) => {
      let m: Msg;
      try { m = JSON.parse(ev.data); } catch { return; }
      if (m.kind === "hello") peerId = m.peer_id;
      listeners.forEach((l) => l(m));
    };
    ws.onerror = () => reject(new Error("p2p ws error"));
    ws.onclose = () => {
      ws = null; openPromise = null; peerId = null;
      // exponential backoff reconnect, capped at 60s
      const wait = Math.min(60000, 1000 * 2 ** attempt++);
      setTimeout(() => { void connect().catch(() => {}); }, wait);
    };
  });
  return openPromise;
}

export function myPeerId(): string | null { return peerId; }

export function onMessage(fn: (m: Msg) => void): () => void {
  listeners.add(fn); return () => listeners.delete(fn);
}

export function send(msg: Record<string, unknown>): void {
  if (ws?.readyState !== WebSocket.OPEN) return;
  ws.send(JSON.stringify(msg));
}

export function advertiseCurrent(): void {
  const ids = listOffline();
  if (ids.length === 0) return;
  send({ kind: "advertise", tracks: ids });
}

/** Ask the server for peers holding a given track. Returns [] on timeout. */
export async function findPeers(trackId: string, timeoutMs = 400): Promise<string[]> {
  await connect().catch(() => {});
  return new Promise((resolve) => {
    let done = false;
    const off = onMessage((m) => {
      if (m.kind !== "peers" || m.track_id !== trackId) return;
      done = true; off(); resolve(m.peers);
    });
    send({ kind: "find", track_id: trackId });
    setTimeout(() => { if (!done) { off(); resolve([]); } }, timeoutMs);
  });
}

/** Hint: kick a connect on first user gesture so we're ready when needed. */
export function warmUp(): void {
  if (typeof window === "undefined") return;
  const trigger = () => { void connect().catch(() => {}); };
  window.addEventListener("pointerdown", trigger, { once: true });
  window.addEventListener("keydown", trigger, { once: true });
}
